// =============================================
// DnsRecordsTab — zakladka "Rekordy DNS" w panelu /cloudflare
//
// Wybor konta -> wybor domeny (zone) -> lista rekordow z CF.
// Dwie sciezki zapisu:
//   1. "Podepnij IP" — jednym klikiem upsert A @ + A www (pointDomainToIp)
//   2. Reczny rekord (A/AAAA/CNAME/TXT/MX/NS) + edycja + usuwanie
// Kazda operacja zapisu -> refetch listy. Token musi miec DNS:Edit.
// =============================================
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, RefreshCw, Plus, Pencil, Trash2, Server, Cloud, CloudOff, Zap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { CloudflareAccount } from "@/hooks/useCloudflareAccounts";
import {
  listZones, listDnsRecords, createDnsRecord, updateDnsRecord, deleteDnsRecord, pointDomainToIp,
  isValidIPv4, CF_DNS_RECORD_TYPES,
  type CfZone, type CfDnsRecord, type CfDnsRecordInput, type CfDnsRecordType,
} from "@/services/cloudflareService";

const errMsg = (err: unknown) => (err instanceof Error ? err.message : String(err));

const emptyForm = (): CfDnsRecordInput => ({ type: "A", name: "@", content: "", proxied: true, ttl: 1, priority: 10, comment: "" });

/** Skraca FQDN do "@" / "www" wzgledem zone — do wyswietlania i edycji. */
const shortName = (fqdn: string, zoneName: string) => {
  const n = fqdn.toLowerCase();
  const z = zoneName.toLowerCase();
  if (n === z) return "@";
  return n.endsWith(`.${z}`) ? n.slice(0, -(z.length + 1)) : n;
};

const ACTION_LABEL: Record<string, string> = { created: "dodano", updated: "zaktualizowano", unchanged: "bez zmian" };

export const DnsRecordsTab = ({ accounts, loading }: { accounts: CloudflareAccount[]; loading: boolean }) => {
  const { toast } = useToast();
  const [accId, setAccId] = useState("");
  const [zones, setZones] = useState<CfZone[]>([]);
  const [zonesBusy, setZonesBusy] = useState(false);
  const [zoneId, setZoneId] = useState("");
  const [records, setRecords] = useState<CfDnsRecord[]>([]);
  const [recordsBusy, setRecordsBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Podepnij IP
  const [ip, setIp] = useState("");
  const [proxied, setProxied] = useState(true);
  const [pointing, setPointing] = useState(false);

  // Dialog dodaj/edytuj
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CfDnsRecord | null>(null);
  const [form, setForm] = useState<CfDnsRecordInput>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const acc = accounts.find((a) => a.id === accId);
  const zone = zones.find((z) => z.id === zoneId);

  // Zmiana konta -> pobierz zone
  useEffect(() => {
    setZones([]); setZoneId(""); setRecords([]); setLoaded(false);
    if (!acc) return;
    let cancelled = false;
    (async () => {
      setZonesBusy(true);
      try {
        const z = await listZones(acc.api_token);
        if (!cancelled) setZones(z);
      } catch (err) {
        if (!cancelled) toast({ title: "Nie udało się pobrać domen", description: errMsg(err), variant: "destructive" });
      } finally {
        if (!cancelled) setZonesBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acc?.id]);

  const fetchRecords = async () => {
    if (!acc || !zone) return;
    setRecordsBusy(true);
    try {
      const r = await listDnsRecords(acc.api_token, zone.id);
      setRecords(r);
      setLoaded(true);
    } catch (err) {
      toast({ title: "Nie udało się pobrać rekordów", description: errMsg(err), variant: "destructive" });
    } finally {
      setRecordsBusy(false);
    }
  };

  // Zmiana zone -> pobierz rekordy
  useEffect(() => {
    setRecords([]); setLoaded(false);
    if (zone) fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneId]);

  const sorted = useMemo(
    () => [...records].sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name)),
    [records],
  );

  const currentA = useMemo(() => {
    if (!zone) return { root: undefined as CfDnsRecord | undefined, www: undefined as CfDnsRecord | undefined };
    const z = zone.name.toLowerCase();
    return {
      root: records.find((r) => r.type === "A" && r.name.toLowerCase() === z),
      www: records.find((r) => r.type === "A" && r.name.toLowerCase() === `www.${z}`),
    };
  }, [records, zone]);

  const wwwCname = useMemo(
    () => (zone ? records.find((r) => r.type === "CNAME" && r.name.toLowerCase() === `www.${zone.name.toLowerCase()}`) : undefined),
    [records, zone],
  );

  const handlePoint = async () => {
    if (!acc || !zone) return;
    if (!isValidIPv4(ip)) {
      toast({ title: "Niepoprawny adres IP", description: "Podaj IPv4, np. 91.123.45.67", variant: "destructive" });
      return;
    }
    if (wwwCname) {
      toast({
        title: "Na www jest rekord CNAME",
        description: "Cloudflare nie pozwala mieć A i CNAME pod tą samą nazwą. Usuń najpierw CNAME dla www z listy poniżej.",
        variant: "destructive",
      });
      return;
    }
    setPointing(true);
    try {
      const res = await pointDomainToIp(acc.api_token, zone.id, zone.name, ip, proxied);
      toast({
        title: `Domena ${zone.name} wskazuje na ${ip.trim()}`,
        description: `@ — ${ACTION_LABEL[res.root.action]}, www — ${ACTION_LABEL[res.www.action]}`,
      });
      await fetchRecords();
    } catch (err) {
      toast({ title: "Nie udało się ustawić rekordów", description: errMsg(err), variant: "destructive" });
    } finally {
      setPointing(false);
    }
  };

  const openAdd = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (r: CfDnsRecord) => {
    if (!zone) return;
    setEditing(r);
    setForm({
      type: (CF_DNS_RECORD_TYPES.includes(r.type as CfDnsRecordType) ? r.type : "A") as CfDnsRecordType,
      name: shortName(r.name, zone.name),
      content: r.content,
      proxied: !!r.proxied,
      ttl: r.ttl ?? 1,
      priority: r.priority ?? 10,
      comment: r.comment ?? "",
    });
    setDialogOpen(true);
  };

  const proxiable = form.type === "A" || form.type === "AAAA" || form.type === "CNAME";

  const handleSave = async () => {
    if (!acc || !zone) return;
    if (!form.name.trim() || !form.content.trim()) return;
    if (form.type === "A" && !isValidIPv4(form.content)) {
      toast({ title: "Niepoprawny adres IPv4", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateDnsRecord(acc.api_token, zone.id, zone.name, editing.id, form);
        toast({ title: "Rekord zaktualizowany" });
      } else {
        await createDnsRecord(acc.api_token, zone.id, zone.name, form);
        toast({ title: "Rekord dodany" });
      }
      setDialogOpen(false);
      await fetchRecords();
    } catch (err) {
      toast({
        title: editing ? "Nie udało się zapisać rekordu" : "Nie udało się dodać rekordu",
        description: errMsg(err),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r: CfDnsRecord) => {
    if (!acc || !zone) return;
    setDeletingId(r.id);
    try {
      await deleteDnsRecord(acc.api_token, zone.id, r.id);
      toast({ title: "Rekord usunięty", description: `${r.type} ${r.name}` });
      setRecords((prev) => prev.filter((x) => x.id !== r.id));
    } catch (err) {
      toast({ title: "Nie udało się usunąć rekordu", description: errMsg(err), variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  if (!loading && accounts.length === 0) {
    return (
      <Card><CardContent className="py-12 text-center text-muted-foreground">
        Najpierw dodaj konto w zakładce <strong>Konta</strong>.
      </CardContent></Card>
    );
  }

  const contentLabel =
    form.type === "A" ? "Adres IPv4"
    : form.type === "AAAA" ? "Adres IPv6"
    : form.type === "CNAME" || form.type === "NS" ? "Cel (domena)"
    : form.type === "MX" ? "Serwer poczty"
    : "Wartość";

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground max-w-3xl">
        Rekordy DNS domeny na Cloudflare. Token konta musi mieć uprawnienie <code>DNS:Edit</code> (samo <code>Zone:Edit</code> pozwala tylko czytać).
      </p>

      {/* Wybór konta + domeny */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-2">
          <Label>Konto Cloudflare</Label>
          <Select value={accId} onValueChange={setAccId}>
            <SelectTrigger className="w-64"><SelectValue placeholder="Wybierz konto" /></SelectTrigger>
            <SelectContent>
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.label}{a.email ? ` · ${a.email}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Domena</Label>
          <Select value={zoneId} onValueChange={setZoneId} disabled={!accId || zonesBusy}>
            <SelectTrigger className="w-72">
              {zonesBusy ? (
                <span className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />pobieram domeny…</span>
              ) : (
                <SelectValue placeholder={accId ? "Wybierz domenę" : "najpierw konto"} />
              )}
            </SelectTrigger>
            <SelectContent>
              {zones.map((z) => (
                <SelectItem key={z.id} value={z.id}>{z.name}{z.status !== "active" ? ` (${z.status})` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={fetchRecords} disabled={!zone || recordsBusy}>
          {recordsBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}Odśwież
        </Button>
      </div>

      {zone && zone.status !== "active" && (
        <p className="text-xs text-amber-400">
          Zone ma status <strong>{zone.status}</strong> — rekordy możesz ustawić już teraz, ale zaczną działać dopiero gdy nameservery u rejestratora wskażą na Cloudflare (panel „Zmiana DNS”).
        </p>
      )}

      {/* Szybka ścieżka: podepnij IP */}
      {zone && loaded && (
        <Card>
          <CardContent className="pt-5 space-y-4">
            <div className="flex items-center gap-2 font-medium"><Zap className="h-4 w-4 text-orange-400" />Podepnij domenę pod serwer</div>
            <p className="text-xs text-muted-foreground">
              Ustawia rekord <code>A</code> dla <strong>{zone.name}</strong> i <strong>www.{zone.name}</strong> na podany adres IP. Istniejące rekordy A zostaną nadpisane.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-2">
                <Label htmlFor="cf-ip">Adres IP serwera (IPv4)</Label>
                <Input id="cf-ip" value={ip} onChange={(e) => setIp(e.target.value)} placeholder="np. 91.123.45.67" className="w-56 font-mono" />
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Switch id="cf-proxied" checked={proxied} onCheckedChange={setProxied} />
                <Label htmlFor="cf-proxied" className="cursor-pointer">Proxy Cloudflare (pomarańczowa chmurka)</Label>
              </div>
              <Button onClick={handlePoint} disabled={pointing || !ip.trim()}>
                {pointing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Server className="h-4 w-4 mr-2" />}Ustaw A dla @ i www
              </Button>
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span>
                Teraz @ → {currentA.root
                  ? <code className="text-foreground">{currentA.root.content}</code>
                  : <span className="text-amber-400">brak rekordu A</span>}
              </span>
              <span>
                Teraz www → {currentA.www
                  ? <code className="text-foreground">{currentA.www.content}</code>
                  : wwwCname
                    ? <span className="text-amber-400">CNAME → {wwwCname.content}</span>
                    : <span className="text-amber-400">brak rekordu A</span>}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista rekordów */}
      {zone && loaded && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Rekordy ({records.length})</p>
            <Button size="sm" variant="outline" onClick={openAdd}><Plus className="h-4 w-4 mr-1" />Dodaj rekord</Button>
          </div>
          {sorted.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">
              Brak rekordów DNS. Użyj „Podepnij domenę” albo „Dodaj rekord”.
            </CardContent></Card>
          ) : (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Typ</TableHead>
                    <TableHead>Nazwa</TableHead>
                    <TableHead>Wartość</TableHead>
                    <TableHead className="w-24">Proxy</TableHead>
                    <TableHead className="w-20">TTL</TableHead>
                    <TableHead className="w-24 text-right">Akcje</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell><Badge variant="outline" className="font-mono text-xs">{r.type}</Badge></TableCell>
                      <TableCell className="font-mono text-sm">
                        {shortName(r.name, zone.name)}<span className="text-muted-foreground text-xs"> .{zone.name}</span>
                      </TableCell>
                      <TableCell className="font-mono text-sm max-w-md truncate" title={r.content}>
                        {r.type === "MX" && r.priority != null ? `${r.priority} ` : ""}{r.content}
                      </TableCell>
                      <TableCell>
                        {r.proxiable ? (
                          r.proxied
                            ? <span className="flex items-center gap-1 text-xs text-orange-400"><Cloud className="h-3 w-3" />proxy</span>
                            : <span className="flex items-center gap-1 text-xs text-muted-foreground"><CloudOff className="h-3 w-3" />DNS only</span>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.ttl === 1 ? "auto" : r.ttl}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(r)} title="Edytuj">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" disabled={deletingId === r.id} title="Usuń">
                                {deletingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Usunąć rekord {r.type} {r.name}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Wartość: <code>{r.content}</code>. Operacja natychmiastowa na Cloudflare — usunięcie rekordu A dla @ lub www wyłączy stronę.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Anuluj</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(r)}>Usuń</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Dialog dodaj / edytuj */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edytuj rekord" : "Nowy rekord DNS"}</DialogTitle>
            <DialogDescription>
              Domena: <strong>{zone?.name}</strong>. Nazwa <code>@</code> = sama domena, <code>www</code> = www.{zone?.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Typ</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v as CfDnsRecordType }))} disabled={!!editing}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CF_DNS_RECORD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="rec-name">Nazwa</Label>
                <Input id="rec-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="@ / www / blog" className="font-mono" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rec-content">{contentLabel}</Label>
              <Input
                id="rec-content"
                value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                placeholder={form.type === "A" ? "91.123.45.67" : form.type === "CNAME" ? "przyklad.pl" : ""}
                className="font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {form.type === "MX" ? (
                <div className="space-y-2">
                  <Label htmlFor="rec-prio">Priorytet</Label>
                  <Input id="rec-prio" type="number" min={0} value={form.priority ?? 10} onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) || 0 }))} />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="rec-ttl">TTL (sek., 1 = auto)</Label>
                  <Input id="rec-ttl" type="number" min={1} value={form.ttl ?? 1} onChange={(e) => setForm((f) => ({ ...f, ttl: Number(e.target.value) || 1 }))} disabled={proxiable && !!form.proxied} />
                </div>
              )}
              {proxiable && (
                <div className="flex items-center gap-2 pt-7">
                  <Switch id="rec-proxied" checked={!!form.proxied} onCheckedChange={(v) => setForm((f) => ({ ...f, proxied: v, ttl: v ? 1 : f.ttl }))} />
                  <Label htmlFor="rec-proxied" className="cursor-pointer">Proxy Cloudflare</Label>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="rec-comment">Komentarz <span className="text-muted-foreground">(opcjonalnie)</span></Label>
              <Input id="rec-comment" value={form.comment ?? ""} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Anuluj</Button>
            <Button onClick={handleSave} disabled={saving || !form.name.trim() || !form.content.trim()}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{editing ? "Zapisz" : "Dodaj"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
